/**
 * Shared membership / beta-cohort helpers for web surfaces (/join, /options).
 *
 * Keeps "Beta is over" copy consistent across pages that show subscription banners.
 *
 * @file src/client/membership-cohort.ts
 */

export interface BetaCohortProfileInput {
  isBeta?: boolean;
  createdAt?: string;
  stripeCustomerId?: string | null;
}

export interface BetaCohortBillingInput {
  subscriptionActive?: boolean;
  stripeCustomerId?: string | null;
}

/**
 * Returns true when signed-in unpaid users should see legacy beta-ended copy.
 */
export function looksLikeBetaCohort(
  profile: BetaCohortProfileInput | null | undefined,
  billing: BetaCohortBillingInput | null | undefined,
  betaCohortCutoffIso: string | null | undefined
): boolean {
  if (billing?.subscriptionActive === true) {
    return false;
  }

  if (profile?.isBeta === true) {
    return true;
  }

  if (typeof betaCohortCutoffIso !== 'string' || typeof profile?.createdAt !== 'string') {
    return false;
  }

  const cutoff = new Date(betaCohortCutoffIso);
  const created = new Date(profile.createdAt);
  if (Number.isNaN(cutoff.getTime()) || Number.isNaN(created.getTime())) {
    return false;
  }

  if (created >= cutoff) {
    return false;
  }

  const fromSession =
    billing && typeof billing.stripeCustomerId === 'string'
      ? billing.stripeCustomerId.trim()
      : '';
  const fromProfile =
    typeof profile.stripeCustomerId === 'string'
      ? profile.stripeCustomerId.trim()
      : '';
  return !fromSession && !fromProfile;
}
